import { Banner, ErrorIcon, Flex, Link, useToast } from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomFooterTemplateFormState } from '@jane/business-admin/types';
import { businessPaths } from '@jane/business-admin/util';

import { ColorsSection } from './components/footer/ColorsSection';
import { CTASection } from './components/footer/CTASection';
import { FooterLinksSection } from './components/footer/FooterLinksSection';
import { ImageSection } from './components/footer/ImageSection';
import { LayoutSection } from './components/footer/LayoutSection';
import { MobileLayoutSection } from './components/footer/MobileLayoutSection';
import { SocialMediaSection } from './components/footer/SocialMediaSection';
import { TextAreaSection } from './components/footer/TextAreaSection';
import { DEFAULT_MOBILE_LAYOUT } from './components/footer/utils';
import { ModalPageWrapper } from './components/ModalPageWrapper';

export const buildFooterErrorMessages = (
  errors: FieldErrors<BloomFooterTemplateFormState>
): {
  link: string;
  message: string;
}[] => {
  const messages: { link: string; message: string }[] = [];
  Object.entries(errors).forEach(([key]) => {
    messages.push({
      link: key,
      message: `${capitalize(key.replaceAll('_', ' '))} is missing required field`,
    });
  });
  return messages;
};

export const PremiumFooterTemplate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isLoading } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const footer = useMemo(() => {
    if (!bloomMenuQuery.data?.footer) return {};
    return bloomMenuQuery.data?.footer;
  }, [bloomMenuQuery.data?.footer]);

  const defaultColors = useMemo(
    () => ({
      primary:
        bloomMenuQuery.data?.theme?.bloom?.colors?.primary?.main ?? '#0E0E0E',
      background:
        bloomMenuQuery.data?.theme?.bloom?.colors?.header?.main ?? '#FFFFFF',
    }),
    [
      bloomMenuQuery.data?.theme?.bloom?.colors?.primary?.main,
      bloomMenuQuery.data?.theme?.bloom?.colors?.header?.main,
    ]
  );

  const formMethods = useForm<BloomFooterTemplateFormState>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      layout: 'align_left',
      status: 'draft',
      ...footer,
      enabled: footer?.enabled ?? true,
      text_content: footer?.text_content || '',
      colors: footer?.colors ?? defaultColors,
      calls_to_action: [
        {
          header: footer?.calls_to_action?.[0]?.header || '',
          body: footer?.calls_to_action?.[0]?.body || '',
          button_label: footer?.calls_to_action?.[0]?.button_label || '',
          destination: footer?.calls_to_action?.[0]?.destination || '',
        },
      ],
      image: footer?.image?.original ?? null,
      use_logo: footer?.use_logo ?? true,
      links: footer?.links || [],
      social_links: footer?.social_links ?? null,
      mobile_layout: footer?.mobile_layout ?? DEFAULT_MOBILE_LAYOUT,
    },
  });
  const {
    getValues,
    control,
    formState: { isValid, errors },
  } = formMethods;
  const formValues = getValues();

  const errorMessages = buildFooterErrorMessages(errors);

  const onPreview = useCallback(() => {
    updateBloomMenu(
      {
        footer: {
          ...formValues,
          social_links: !formValues.social_links?.links?.length
            ? null
            : formValues.social_links,
          status: 'draft',
        },
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          if (!bloomMenuQuery.data?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuQuery.data.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [bloomMenuQuery.data, formValues, toast, updateBloomMenu]);

  const onPublish = useCallback(
    ({ use_logo, image, ...data }: BloomFooterTemplateFormState) => {
      updateBloomMenu(
        {
          footer: {
            ...data,
            social_links: !data.social_links?.links?.length
              ? null
              : data.social_links,
            use_logo,
            //  1. :image is not in the payload -> keep existing image
            //  2. :image is in the payload -> update existing image
            //  3. :image is explicitly nil in the payload -> destroy existing image
            ...(!use_logo ? { image } : {}),
            status: 'published',
          },
        },
        {
          onError: () => {
            toast.add({
              label: 'An error occurred.',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.add({
              label: 'Menu updated!',
              variant: 'success',
            });
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [toast, updateBloomMenu, navigate]
  );

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading}
      onSubmit={onPublish}
      toggle={{
        control,
        name: 'enabled',
        label: 'Visible',
      }}
      preview={{
        onClick: onPreview,
        disable: isLoading || !isValid,
        label: 'Preview',
      }}
      title="Footer"
    >
      <Flex
        flexDirection="column"
        p={32}
        mt={8}
        gap={24}
        maxWidth={1200}
        mx="auto"
      >
        {errorMessages.length > 0 &&
          errorMessages.map(({ link, message }) => (
            <Banner
              key={link}
              actions={
                <Link
                  onClick={() =>
                    document
                      .getElementById(link)
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  mr={24}
                >
                  View
                </Link>
              }
              icon={<ErrorIcon />}
              label={message}
              variant="error"
            />
          ))}

        <LayoutSection />
        <ColorsSection defaultColors={defaultColors} />
        <CTASection />
        <ImageSection logoUrl={bloomMenuQuery.data?.logo?.original ?? ''} />
        <FooterLinksSection />
        <SocialMediaSection />
        <TextAreaSection />
        <MobileLayoutSection />
      </Flex>
    </ModalPageWrapper>
  );
};
